// /classes: the classes I teach and the ones I am in, as /shares lists
// shares — rows under "Teaching" and "Joined", both headed whenever there is
// a class at all, the empty one saying so; a class's own actions behind its ⋯,
// deleting one I teach among them (byj-56x). Creating one and joining one by
// its code are each a dialog from the page's head (RFC-043 M3, byj-1wv.4,
// byj-1wv.51.6).
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { api, DefaultClassPolicy, type ClassSummary } from "../api";
import { useConfirm } from "../components/Confirm";
import { Link } from "../components/Nav";
import { Page, PageHead } from "../components/Page";
import { AssignmentFields, assignmentIn, blankDraft, type AssignmentDraft } from "../components/AssignmentFields";
import { useModalKeys } from "../components/Modal";
import { EmptyRows, Grouped, initialOf } from "../components/Rows";
import { RowMenu, type MenuItem } from "../components/RowMenu";
import { navigate } from "../router";
import { t, word } from "../i18n";
import { JoinView, useJoin } from "../components/JoinView";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

function ClassRow({ c, onDelete }: { c: ClassSummary; onDelete: (c: ClassSummary) => void }) {
  const path = `/c/${encodeURIComponent(c.id)}`;
  const teaching = c.role === "instructor";
  // a class's own actions, its instructor's alone (byj-56x)
  const menu: MenuItem[] = teaching
    ? [
        { label: t("classes:manage"), onSelect: () => navigate(`${path}/manage`) },
        { label: t("classes:previewAsStudent"), onSelect: () => navigate(`${path}/preview`) },
        { label: t("classes:deleteClass"), onSelect: () => onDelete(c), danger: true },
      ]
    : [];
  return (
    <li className="share-row class-list-row is-live">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        {initialOf(c.name)}
      </span>
      <div className="row-main">
        <Link to={path} className="row-title">
          {c.name}
        </Link>
        <div className="row-sub">
          {t("classes:members", { count: c.members })}
          {c.join_code && (
            <>
              {" · "}
              <code>{t("classes:classCode", { code: c.join_code })}</code>
            </>
          )}
        </div>
      </div>
      <div className="row-meta">
        <span className={`chip ${teaching ? "chip-live" : ""}`}>{word("classes:classRole", c.role) ?? c.role}</span>
      </div>
      <RowMenu label={t("shares:moreFor", { title: c.name })} items={menu} />
    </li>
  );
}

/**
 * Creating a class, a dialog over the page as its other short questions are
 * (Confirm, EditTitle): its name, and — folded under "+ Add assignments" —
 * the assignments to publish with it, each with what to do and when it is
 * due as the management page asks them; on to its management page. Esc, Cancel,
 * its ✕ or a press outside closes it, the focus going back to New class.
 */
function NewClassDialog({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // assignments published with the class (RFC-043 model 19): folded until
  // asked for, and rows left blank are not sent
  const [withAssignments, setWithAssignments] = useState(false);
  const [rows, setRows] = useState<AssignmentDraft[]>([blankDraft()]);
  const setRow = (i: number, p: Partial<AssignmentDraft>) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...p } : r)));
  const nameField = useRef<HTMLInputElement>(null);
  const titles = useRef<(HTMLInputElement | null)[]>([]);
  // a row removed, the focus goes to the title of the row before it, not out of the dialog with the button
  const [focusRow, setFocusRow] = useState<number | null>(null);
  useEffect(() => {
    if (focusRow === null) return;
    titles.current[focusRow]?.focus();
    setFocusRow(null);
  }, [focusRow]);

  const keys = useModalKeys(onClose);
  useEffect(() => {
    nameField.current?.focus();
  }, []);
  useEffect(() => {
    if (withAssignments) titles.current[0]?.focus();
  }, [withAssignments]);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const c = await api.createClass(
        name.trim(),
        DefaultClassPolicy,
        withAssignments ? rows.filter((r) => r.title.trim()).map(assignmentIn) : [],
      );
      navigate(`/c/${encodeURIComponent(c.id)}/manage`);
    } catch (err) {
      setError(errText(err));
      setBusy(false);
    }
  };
  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <form id="new-class-dialog" className="modal new-class-modal stack" role="dialog" aria-modal="true" aria-labelledby="new-class-title" onKeyDown={keys} onSubmit={(e) => void create(e)}>
        <div className="row">
          <h3 id="new-class-title" className="grow flush">
            {t("classes:createClassTitle")}
          </h3>
          <button type="button" className="small" aria-label={t("common:close")} onClick={onClose}>
            ✕
          </button>
        </div>
        {error && <p className="error">{t("common:error", { m: error })}</p>}
        <label className="field">
          {t("classes:className")}
          <input ref={nameField} value={name} onChange={(e) => setName(e.target.value)} placeholder={t("classes:classNamePlaceholder")} maxLength={120} />
        </label>
        {withAssignments ? (
          <fieldset className="stack" data-testid="new-assignments">
            <legend className="small">{t("classes:createWithAssignments")}</legend>
            {rows.map((r, i) => (
              <div className="new-assignment stack" key={i} data-testid="new-assignment">
                <AssignmentFields
                  draft={r}
                  onChange={(p) => setRow(i, p)}
                  n={i + 1}
                  titleRef={(el) => {
                    titles.current[i] = el;
                  }}
                  extra={
                    rows.length > 1 && (
                      <button
                        type="button"
                        className="small"
                        onClick={() => {
                          setRows((rs) => rs.filter((_, j) => j !== i));
                          setFocusRow(Math.max(0, i - 1));
                        }}
                      >
                        {t("common:removeRow")}
                      </button>
                    )
                  }
                />
              </div>
            ))}
            <button type="button" className="small" onClick={() => setRows((rs) => [...rs, blankDraft()])}>
              {t("common:addAnother")}
            </button>
          </fieldset>
        ) : (
          <button type="button" className="small add-assignments" aria-expanded={false} onClick={() => setWithAssignments(true)}>
            {t("classes:addAssignments")}
          </button>
        )}
        <div className="row modal-actions">
          <button type="button" onClick={onClose}>
            {t("common:cancel")}
          </button>
          <button className="primary" type="submit" disabled={busy || !name.trim()}>
            {busy ? t("classes:creating") : t("classes:createClass")}
          </button>
        </div>
      </form>
    </div>
  );
}

/** A join code as typed or pasted: a join link's own code when the link is pasted whole, upper-cased, no spaces. */
export function codeFrom(input: string): string {
  const s = input.trim();
  const m = s.match(/[?&]code=([^&#\s]+)/);
  let raw = s;
  if (m) {
    try {
      raw = decodeURIComponent(m[1]!);
    } catch {
      raw = m[1]!;
    }
  }
  return raw.replace(/\s+/g, "").toUpperCase();
}

/**
 * Joining a class by its code, a dialog as creating one is: the code — typed,
 * or a join link pasted — checked where it is typed (one the person is
 * already in said so with the way to it, from the list they have; one no
 * class has said so); then, in the same dialog, what joining means in the
 * join page's own words (JoinView), and "I agree and join", on to the
 * class's page. A consent changed under the reader is shown anew and asked
 * again, as the join page does. Esc, its ✕ or a press outside closes it, the
 * focus going back to Join with a code.
 */
function JoinDialog({ classes, onClose }: { classes: ClassSummary[]; onClose: () => void }) {
  const [entered, setEntered] = useState("");
  const [already, setAlready] = useState<ClassSummary | null>(null);
  const { preview, error, stale, busy, look, agree: join, forget, setError } = useJoin();
  const codeField = useRef<HTMLInputElement>(null);

  const keys = useModalKeys(onClose);
  // the focus inside it as each step opens, so Tab stays in it: the code
  // first, and again on going back to it; what joining means, from its heading
  useEffect(() => {
    if (preview) document.getElementById("join-class-title")?.focus();
    else codeField.current?.focus();
  }, [preview]);

  const check = async (e: FormEvent) => {
    e.preventDefault();
    const code = codeFrom(entered);
    if (!code) return;
    setError(null);
    setAlready(null);
    const mine = classes.find((c) => c.join_code?.toUpperCase() === code);
    if (mine) {
      setAlready(mine);
      return;
    }
    await look(code);
  };

  const agree = async () => {
    const c = await join();
    if (c) navigate(`/c/${encodeURIComponent(c.id)}`);
  };

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      {preview ? (
        <div id="join-class-dialog" className="modal join-modal stack" role="dialog" aria-modal="true" aria-labelledby="join-class-title" onKeyDown={keys}>
          <div className="row">
            <button type="button" className="small" onClick={forget}>
              {t("classes:backToCode")}
            </button>
            <span className="grow" />
            <button type="button" className="small" aria-label={t("common:close")} onClick={onClose}>
              ✕
            </button>
          </div>
          {error && <p className="error">{error}</p>}
          <JoinView preview={preview} stale={stale} busy={busy} onAgree={() => void agree()} titleId="join-class-title" />
        </div>
      ) : (
        <form id="join-class-dialog" className="modal join-modal stack" role="dialog" aria-modal="true" aria-labelledby="join-class-title" onKeyDown={keys} onSubmit={(e) => void check(e)}>
          <div className="row">
            <h3 id="join-class-title" className="grow flush">
              {t("classes:joinClassTitle")}
            </h3>
            <button type="button" className="small" aria-label={t("common:close")} onClick={onClose}>
              ✕
            </button>
          </div>
          <p className="muted small flush">{t("classes:joinHelp")}</p>
          <div className="join-code-row">
            <input
              ref={codeField}
              className="code-input"
              aria-label={t("classes:joinCode")}
              value={entered}
              onChange={(e) => {
                // a code shows as it is sent; a link pasted is left as it is
                const v = e.target.value;
                setEntered(/[/?]/.test(v) ? v : v.toUpperCase());
                setError(null);
                setAlready(null);
              }}
              placeholder={t("classes:joinCodePlaceholder")}
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
            />
            <button type="submit" className="primary" disabled={busy || !codeFrom(entered)}>
              {busy ? t("classes:checkingCode") : t("classes:continueJoin")}
            </button>
          </div>
          {error && (
            <p className="error flush" role="alert">
              {error}
            </p>
          )}
          {already && (
            <p className="flush" role="status" data-testid="already-in">
              {t("classes:alreadyIn", { name: already.name })} <Link to={`/c/${encodeURIComponent(already.id)}`}>{t("classes:openClassPage")}</Link>
            </p>
          )}
        </form>
      )}
    </div>
  );
}

/** My classes; own: the person makes and joins classes here, as a third party's people do not (byj-1wv.117.10). */
export function Classes({ own = true }: { own?: boolean } = {}) {
  const [classes, setClasses] = useState<ClassSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirm, confirmDialog] = useConfirm();
  const [creating, setCreating] = useState(false);
  const newButton = useRef<HTMLButtonElement>(null);
  // closed, the focus goes back to New class
  const closeNew = useCallback(() => {
    setCreating(false);
    newButton.current?.focus();
  }, []);
  const [joining, setJoining] = useState(false);
  const joinButton = useRef<HTMLButtonElement>(null);
  // closed, the focus goes back to Join with a code
  const closeJoin = useCallback(() => {
    setJoining(false);
    joinButton.current?.focus();
  }, []);

  const load = useCallback(async () => {
    setError(null);
    try {
      setClasses(await api.classes());
    } catch (err) {
      setError(errText(err));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const remove = async (c: ClassSummary) => {
    if (!(await confirm(t("classes:deleteClassConfirm", { name: c.name }), { action: t("classes:deleteClass"), danger: true }))) return;
    setError(null);
    try {
      await api.deleteClass(c.id);
      setClasses((cs) => (cs ? cs.filter((x) => x.id !== c.id) : cs));
    } catch (err) {
      setError(errText(err));
    }
  };

  const all = classes ?? [];
  return (
    <Page>
      {confirmDialog}
      <PageHead
        title={t("classes:classesTitle")}
        actions={
          own && (
            <>
              <button ref={joinButton} aria-haspopup="dialog" aria-expanded={joining} aria-controls={joining ? "join-class-dialog" : undefined} onClick={() => setJoining(true)}>
                {t("classes:joinWithCode")}
              </button>
              <button ref={newButton} className="primary" aria-haspopup="dialog" aria-expanded={creating} aria-controls={creating ? "new-class-dialog" : undefined} onClick={() => setCreating(true)}>
                {t("classes:newClass")}
              </button>
            </>
          )
        }
      />
      {error && (
        <p className="error">
          {t("common:error", { m: error })} <button className="small" onClick={() => void load()}>{t("common:retry")}</button>
        </p>
      )}
      {classes === null ? (
        !error && <p className="muted">{t("common:loading")}</p>
      ) : all.length === 0 ? (
        <p className="muted">{t("classes:noClasses")}</p>
      ) : (
        <div className="rows-panel class-groups" data-testid="class-list">
          <Grouped
            id="classes"
            headed
            groups={[
              { title: t("classes:teaching"), items: all.filter((c) => c.role === "instructor"), none: <EmptyRows text={t("classes:noTeaching")} /> },
              { title: t("classes:joinedClasses"), items: all.filter((c) => c.role !== "instructor"), none: <EmptyRows text={t("classes:noJoined")} /> },
              // one who joins no class here is shown no empty group asking them to
            ].filter((g, i) => own || i === 0 || g.items.length > 0)}
            row={(c) => <ClassRow key={c.id} c={c} onDelete={(x) => void remove(x)} />}
          />
        </div>
      )}
      {creating && <NewClassDialog onClose={closeNew} />}
      {joining && <JoinDialog classes={classes ?? []} onClose={closeJoin} />}
    </Page>
  );
}
